import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MaybeUndefined } from 'src/app/utils';
import AppValues from '../../../../common/app.values';
import ProjectLinks from '../../../../common/project.links';
import { SelectorOption } from '../../../../interfaces/form-and-field.interface';
import { ActionBtnInfo, EntityStatus, ItemStatus } from '../../../../interfaces/views.interface';
import { SearchResult } from '../../../../swagger-api';
import { CardType, InviteCardType } from '../../../../views/cards/cards.types';
import { PaginationComponentShared } from '../../../shared/pagination/pagination-component.shared';
import { SearchCategories, SearchResultSorting, SortByOptions } from '../../search.enum';

const sortResultMapping: Record<SearchResultSorting, SortByOptions> = {
  [SearchResultSorting.ASCENDING]: SortByOptions.NAME_A_Z,
  [SearchResultSorting.DESCENDING]: SortByOptions.NAME_Z_A,
  [SearchResultSorting.NEWEST]: SortByOptions.NEWEST,
  [SearchResultSorting.OLDEST]: SortByOptions.OLDEST,
  [SearchResultSorting.RELEVANCE]: SortByOptions.SCORE,
};

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['../search-results-shared/search-results-shared.component.scss'],
  providers: [DecimalPipe],
})
export class SearchResultsComponent extends PaginationComponentShared implements OnInit, OnChanges {
  @Input({ required: true }) public isSearchActive = false;
  @Input({ required: true }) public isAuthenticated = false;
  @Input({ required: true }) public isLoading = false;
  @Input({ required: true }) public results: SearchResult[] = [];
  @Input({ required: true }) public options: SelectorOption[] = [];

  @Input({ required: false }) public inviteCardType: MaybeUndefined<InviteCardType>;

  @Output() public action: EventEmitter<ActionBtnInfo> = new EventEmitter<ActionBtnInfo>();
  @Output() public sortCategory: EventEmitter<string> = new EventEmitter<string>();

  public readonly DELETED: EntityStatus = ItemStatus.DELETED;
  public readonly EMPTY: EntityStatus = ItemStatus.EMPTY;
  public readonly DIGITS_REPRESENTATION_OPTIONS: string = AppValues.DIGITS_REPRESENTATION_OPTIONS;
  public readonly CARD_TYPES_BY_ENTITY_TYPE: { [entityType: string]: CardType } = {
    Community: CardType.COMMUNITY,
    Project: CardType.PROJECT,
    Post: CardType.POST,
    User: CardType.USER,
    Comment: CardType.COMMENT,
  };

  public searchResultHint = '';
  public selectedSearchOption: SortByOptions;

  private translateService = inject(TranslateService);
  private decimalPipe = inject(DecimalPipe);

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && this.results) {
      this.count = this.searchState.pagination.totalItemsCount;
      this.setSearchResultHint();
    }
  }

  public ngOnInit(): void {
    this.innerWidth = window.innerWidth;
    this.selectedSearchOption = SortByOptions.SCORE;
  }

  public onAction(btnInfo: ActionBtnInfo): void {
    this.action.emit(btnInfo);
  }

  public onSortCategory(sortCategory: SearchResultSorting): void {
    this.selectedSearchOption = sortResultMapping[sortCategory];
    this.sortCategory.emit(sortCategory);
  }

  /**
   * @description Called on component input params changes for 'search' page, overwrites
   *              cound and searchResultHint values depend on count value.
   */
  private setSearchResultHint(): void {
    const selected: SelectorOption = this.options.find(
      (option: SelectorOption) => option.entity_id === this.searchState.params.itemKind,
    );
    const hint: string = selected ? (this.translateService.instant(selected.title) as string) : '';
    this.searchResultHint = this.translateService.instant(
      this.count > 0
        ? 'SEARCH_RESULTS_HINT'
        : hint === SearchCategories.POSTS
          ? 'SEARCH_NO_RESULTS_POST_HINT'
          : this.isAuthenticated
            ? 'SEARCH_NO_RESULTS_HINT'
            : 'SEARCH_NO_RESULTS_HINT_UNAUTHENTICATED',
      {
        value: this.searchState.params.queryText,
        option: hint,
        logIn: ProjectLinks.LINK_LOGIN,
        signUp: ProjectLinks.LINK_REGISTRATION,
        count: this.decimalPipe.transform(this.count, this.DIGITS_REPRESENTATION_OPTIONS),
      },
    ) as string;
  }
}
